// Payments-specific table state mappings
import { getSingleValue, getArrayValue } from '@/lib/utils/table-state-utils';

// Filter mappings for payments - maps column IDs to tRPC input format
export const paymentsFilterMapping = {
    // Note: Search is handled separately via the search parameter (full-text search)
    // It searches across payment ID, customer email, first name, and last name

    // Amount filter with conditions - values are in minor units (cents)
    amount: (value: any) => {
        if (value && typeof value === 'object' && 'condition' in value) {
            // Handle condition filter { condition: 'is-greater-than', value: [22] }
            return { amount: value };
        }
        return undefined;
    },

    // Status filter - supports multiple status values
    status: (value: any) => {
        const values = getArrayValue(value);
        // If single value, pass as single enum, if multiple pass first one
        // Note: backend supports single status filter
        return values.length > 0 ? { status: values[0] as any } : undefined;
    },

    // Initiated by filter
    initiatedBy: (value: any) => {
        const values = getArrayValue(value);
        return values.length > 0 ? { initiatedBy: values[0] as any } : undefined;
    },

    // Reason type filter
    reasonType: (value: any) => {
        const values = getArrayValue(value);
        return values.length > 0 ? { reasonType: values[0] as any } : undefined;
    },

    // Require action filter
    requireAction: (value: any) => {
        const values = getArrayValue(value);
        return values.length > 0 ? { requireAction: values[0] as any } : undefined;
    },

    // Draft filter
    draft: (value: any) => ({
        draft: value === 'true' || value === true
    }),

    // Payment instrument filter
    paymentInstrumentId: (value: any) => ({
        paymentInstrumentId: getSingleValue(value)
    }),

    // Card filter - maps to card last4
    card: (value: any) => ({
        card: { last4: getSingleValue(value) }
    }),

    // Payment method filter - maps to paymentInstruments.type (apple_pay / google_pay / card)
    paymentMethod: (value: any) => {
        const values = getArrayValue(value);
        return values.length > 0 ? { paymentMethod: values } : undefined;
    },

    // BIN filter - maps to card bin (from cardEnrichments)
    bin: (value: any) => ({
        card: { bin: getSingleValue(value) }
    }),

    // Currency filter - supports multiple currencies
    currency: (value: any) => ({
        currency: getArrayValue(value)
    }),

    // Date range filter - pass through directly like orders
    createdAt: (value: any) => ({
        date: value
    }),

    // Chain Index filter - range filter
    chainIndex: (value: any) => {
        if (value && typeof value === 'object') {
            // Handle range filter [min, max]
            if (Array.isArray(value) && value.length === 2) {
                const [min, max] = value;
                return {
                    chainIndex: {
                        min: min !== undefined && min !== null ? Number(min) : undefined,
                        max: max !== undefined && max !== null ? Number(max) : undefined,
                    }
                };
            }
        }
        return undefined;
    },

    // Retry Index filter - range filter
    retryIndex: (value: any) => {
        if (value && typeof value === 'object') {
            // Handle range filter [min, max]
            if (Array.isArray(value) && value.length === 2) {
                const [min, max] = value;
                return {
                    retryIndex: {
                        min: min !== undefined && min !== null ? Number(min) : undefined,
                        max: max !== undefined && max !== null ? Number(max) : undefined,
                    }
                };
            }
        }
        return undefined;
    },

    // Processor filter
    processor: (value: any) => ({
        processorName: getSingleValue(value)
    }),

    // Store filter - supports multiple stores  
    store: (value: any) => ({
        storeIds: getArrayValue(value)
    }),
};

export const paymentsQueryParamsMapping = {
    amount: (value: URLSearchParams) => {
        const res = value.get('amount')
        return res ? JSON.parse(res) : null
    },
    status: (value: URLSearchParams) => {
        return value.get('status')?.split(',')
    },
    initiatedBy: (value: URLSearchParams) => {
        return value.get('initiatedBy')?.split(',')
    },
    reasonType: (value: URLSearchParams) => {
        return value.get('reasonType')?.split(',')
    },
    requireAction: (value: URLSearchParams) => {
        return value.get('requireAction')?.split(',')
    },
    draft: (value: URLSearchParams) => {
        return value.get('draft')
    },
    paymentInstrumentId: (value: URLSearchParams) => {
        return value.get('paymentInstrumentId')
    },
    card: (value: URLSearchParams) => {
        return value.get('card')
    },
    paymentMethod: (value: URLSearchParams) => {
        return value.get('paymentMethod')?.split(',')
    },
    bin: (value: URLSearchParams) => {
        return value.get('bin')
    },
    currency: (value: URLSearchParams) => {
        return value.get('currency')?.split(',')
    },
    createdAt: (value: URLSearchParams) => {
        const res = value.get('createdAt')
        return res ? JSON.parse(res) : null
    },
    chainIndex: (value: URLSearchParams) => {
        return value.get('chainIndex')?.split(',')
    },
    retryIndex: (value: URLSearchParams) => {
        return value.get('retryIndex')?.split(',')
    },
    processor: (value: URLSearchParams) => {
        return value.get('processor')
    },
    store: (value: URLSearchParams) => {
        return value.get('store')?.split(',')
    },
}

// Sort field mappings for payments (frontend column ID -> backend sort field)
export const paymentsSortMapping = {
    id: 'id' as const,
    createdAt: 'createdAt' as const,
    status: 'status' as const,
    amount: 'amount' as const,
    currency: 'currency' as const,
    chainIndex: 'chainIndex' as const,
    retryIndex: 'retryIndex' as const,
    initiatedBy: 'initiatedBy' as const,
};
